// src/pages/nafed/pmc/PmcPacsDocumentScrutiny.js
// Simplified PMC document desk: view/download/archive/replace/ZIP -> direct MOU.

import React, { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";

import { auth, db, functions, storage } from "../../../firebaseConfig";
import { NAFED_COLLECTIONS } from "./nafedCollections";

const MANDATORY_DOCUMENTS = [
  {
    id: "COMMITTEE_RESOLUTION",
    code: "RES",
    title: "District Registrar Application & Managing Committee Resolution",
  },
  {
    id: "MARGIN_MONEY",
    code: "MMC",
    title: "Margin Money Certificate",
  },
  {
    id: "STATUTORY_PROCEEDINGS",
    code: "SPC",
    title: "No Statutory Proceedings Certificate",
  },
  {
    id: "NO_IRREGULARITY",
    code: "NIC",
    title: "No Financial Irregularity Certificate",
  },
  {
    id: "CONTRIBUTION_UNDERTAKING",
    code: "GIU",
    title: "Godown Insurance / Project Contribution Undertaking",
  },
];

const clean = (value) => String(value ?? "").trim();
const upper = (value) => clean(value).toUpperCase();

const safeFileName = (value, fallback = "document.pdf") => {
  const name = clean(value || fallback)
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .slice(0, 150);
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
};

const safeZipName = (value, fallback = "PACS_Documents.zip") => {
  const name = clean(value || fallback)
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .slice(0, 150);
  return /\.zip$/i.test(name) ? name : `${name}.zip`;
};

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = value?.toDate instanceof Function ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPacsName = (project = {}) =>
  clean(
    project.pacsName ||
      project.societyName ||
      project.organizationName ||
      "PACS / Society",
  );

const getApplicationId = (project = {}) =>
  clean(project.applicationId || project.projectId || project.id || "-");

const isNafedProject = (project = {}) =>
  project.nafedProject === true ||
  upper(project.projectSource) === "NAFED" ||
  upper(project.applicationPortal) === "NAFED_PACS_APPLY";

const isActiveProject = (project = {}) =>
  project.isDeleted !== true &&
  project.deleted !== true &&
  !["DELETED", "REMOVED", "ARCHIVED", "TRASHED"].includes(
    upper(project.status || project.applicationStatus),
  );

const recordUrl = (record = {}) =>
  clean(
    record.url ||
      record.downloadURL ||
      record.downloadUrl ||
      record.fileUrl ||
      record.signedFileUrl,
  );

const documentSource = (record = {}, sourceKey = "") => {
  if (sourceKey === "pmcVerificationDocuments") return "PMC replacement";
  if (/uploaded/i.test(clean(record.source))) return "PACS uploaded";
  if (/generated/i.test(clean(record.source))) return "System generated";
  if (sourceKey === "signedPacsDocuments") return "PACS signed set";
  if (sourceKey === "generatedDocuments") return "System generated";
  return "PACS document";
};

const activeDocument = (project = {}, type) => {
  if (project.pmcDocumentOverrides?.[type]?.action === "REMOVED") return null;

  const sources = [
    ["pmcVerificationDocuments", project.pmcVerificationDocuments?.[type]],
    ["signedPacsDocuments", project.signedPacsDocuments?.[type]],
    ["generatedDocuments", project.generatedDocuments?.[type]],
  ];

  for (const [sourceKey, record] of sources) {
    if (recordUrl(record)) {
      return {
        ...record,
        url: recordUrl(record),
        sourceKey,
        sourceLabel: documentSource(record, sourceKey),
      };
    }
  }
  return null;
};

const allDocumentDefinitions = (project = {}) => {
  const mandatoryIds = new Set(MANDATORY_DOCUMENTS.map((item) => item.id));
  const discovered = new Set();

  [
    project.generatedDocuments,
    project.signedPacsDocuments,
    project.pmcVerificationDocuments,
  ].forEach((map) => {
    Object.keys(map || {}).forEach((key) => discovered.add(key));
  });

  const extras = [...discovered]
    .filter((id) => !mandatoryIds.has(id))
    .map((id) => {
      const record = activeDocument(project, id) || {};
      return {
        id,
        code: clean(record.code || id.slice(0, 3)).toUpperCase(),
        title: clean(record.title || record.documentTitle || id.replace(/_/g, " ")),
        optional: true,
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  return [...MANDATORY_DOCUMENTS, ...extras];
};

const sha256File = async (file) => {
  if (!globalThis.crypto?.subtle) return "";
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const isMouLocked = (project = {}) => {
  const signatures = project.mouSignatures || {};
  const anySigned = Object.values(signatures).some(
    (item) => upper(item?.status) === "SIGNED",
  );
  return (
    anySigned ||
    project.mouFullyExecuted === true ||
    project.twoPartyMouExecuted === true ||
    project.threePartyMouExecuted === true ||
    Boolean(
      project.finalExecutedMouUrl ||
        project.finalMouUrl ||
        project.finalSignedMouUrl ||
        project.mouFinalPdfUrl,
    ) ||
    [
      "FULLY_EXECUTED",
      "MOU_FULLY_EXECUTED",
      "TWO_PARTY_MOU_FULLY_EXECUTED",
      "THREE_PARTY_MOU_FULLY_EXECUTED",
      "MOU_COMPLETED",
    ].includes(
      upper(project.mouStage || project.mouStatus),
    )
  );
};

const permissionMessage = (error, fallback) => {
  if (error?.code === "functions/not-found") {
    return "Approval service is not deployed. Deploy approvePmcDocumentsAndOpenMou in us-central1.";
  }
  if (
    error?.code === "functions/unauthenticated" ||
    error?.code === "functions/permission-denied"
  ) {
    return error?.message || "The secure approval service rejected this PMC session.";
  }
  if (
    error?.code === "permission-denied" ||
    error?.code === "firestore/permission-denied" ||
    error?.code === "storage/unauthorized"
  ) {
    return "This document action was rejected by Firebase security.";
  }
  return error?.message || fallback;
};

export default function PmcPacsDocumentScrutiny({
  scope = {},
  profile = {},
  onOpenMou,
  onProjectUpdated,
}) {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const allowedProjectIds = useMemo(() => {
    const ids = scope.allowedProjectIds || scope.assignedProjectIds || [];
    return [...new Set(ids.map(clean).filter(Boolean))];
  }, [scope.allowedProjectIds, scope.assignedProjectIds]);

  const loadProjects = async (preserveSelection = true) => {
    try {
      setLoading(true);
      if (!allowedProjectIds.length) {
        setProjects([]);
        setSelectedId("");
        return;
      }

      const rows = await Promise.all(
        allowedProjectIds.map(async (projectId) => {
          try {
            const snapshot = await getDoc(
              doc(
                db,
                scope.collectionName || NAFED_COLLECTIONS.godownProjects,
                projectId,
              ),
            );
            if (!snapshot.exists()) return null;
            const project = { id: snapshot.id, ...snapshot.data() };
            if (!isNafedProject(project) || !isActiveProject(project)) return null;
            if (
              typeof scope.filterProject === "function" &&
              !scope.filterProject(project)
            ) {
              return null;
            }
            return project;
          } catch (error) {
            console.warn(`Project ${projectId} could not be loaded`, error);
            return null;
          }
        }),
      );

      const records = rows.filter(Boolean).sort((a, b) =>
        clean(getPacsName(a)).localeCompare(clean(getPacsName(b))),
      );
      setProjects(records);
      if (!preserveSelection || !records.some((row) => row.id === selectedId)) {
        setSelectedId(records[0]?.id || "");
      }
    } catch (error) {
      console.error(error);
      toast.error(permissionMessage(error, "PACS documents could not be loaded."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedProjectIds.join("|")]);

  const project = useMemo(
    () => projects.find((item) => item.id === selectedId) || null,
    [projects, selectedId],
  );

  const definitions = useMemo(
    () => (project ? allDocumentDefinitions(project) : MANDATORY_DOCUMENTS),
    [project],
  );

  const rows = useMemo(
    () =>
      definitions.map((definition) => ({
        ...definition,
        record: project ? activeDocument(project, definition.id) : null,
      })),
    [definitions, project],
  );

  const availableCount = rows.filter((row) => row.record?.url).length;
  const mandatoryMissing = MANDATORY_DOCUMENTS.filter(
    (item) => !project || !activeDocument(project, item.id)?.url,
  );
  const mouLocked = Boolean(project && isMouLocked(project));

  const patchLocalProject = (projectId, changes) => {
    setProjects((current) =>
      current.map((item) =>
        item.id === projectId ? { ...item, ...changes } : item,
      ),
    );
    onProjectUpdated?.(projectId, changes);
  };

  const downloadDocument = async (row) => {
    if (!row.record?.url) return toast.error("Document is not available.");
    try {
      setBusy(`DOWNLOAD_${row.id}`);
      const response = await fetch(row.record.url);
      if (!response.ok) throw new Error(`Download failed (${response.status}).`);
      const blob = await response.blob();
      saveAs(
        blob,
        safeFileName(
          row.record.fileName,
          `${row.code}_${getApplicationId(project)}.pdf`,
        ),
      );
    } catch (error) {
      console.error(error);
      window.open(row.record.url, "_blank", "noopener,noreferrer");
      toast.error("Direct download was blocked; the document was opened instead.");
    } finally {
      setBusy("");
    }
  };

  const downloadAllAsZip = async () => {
    if (!project || !availableCount) return toast.error("No document is available.");
    try {
      setBusy("ZIP");
      const zip = new JSZip();
      const usedNames = new Set();

      for (const row of rows.filter((item) => item.record?.url)) {
        const response = await fetch(row.record.url);
        if (!response.ok) {
          throw new Error(`${row.title} could not be downloaded (${response.status}).`);
        }
        const blob = await response.blob();
        let name = safeFileName(
          row.record.fileName,
          `${row.code}_${getApplicationId(project)}.pdf`,
        );
        let suffix = 2;
        while (usedNames.has(name.toLowerCase())) {
          name = safeFileName(`${row.code}_${suffix}_${getApplicationId(project)}.pdf`);
          suffix += 1;
        }
        usedNames.add(name.toLowerCase());
        zip.file(name, blob);
      }

      zip.file(
        "DOCUMENT_INDEX.txt",
        rows
          .filter((item) => item.record?.url)
          .map(
            (item, index) =>
              `${index + 1}. ${item.title}\r\n   Source: ${item.record.sourceLabel}\r\n   Version: ${item.record.signedVersion || item.record.version || 1}`,
          )
          .join("\r\n\r\n"),
      );

      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(
        blob,
        safeZipName(
          `PACS_${getApplicationId(project)}_Documents.zip`,
          "PACS_Documents.zip",
        ),
      );
      toast.success(`${availableCount} documents downloaded in one ZIP.`);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "ZIP download failed.");
    } finally {
      setBusy("");
    }
  };

  const replaceDocument = async (row, file) => {
    const user = auth.currentUser;
    if (!project || !user || !file) return;
    if (mouLocked) return toast.error("MOU signing has started; documents are locked.");
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      return toast.error("Only PDF files are allowed.");
    }
    if (file.size > 15 * 1024 * 1024) {
      return toast.error("PDF size must be 15 MB or less.");
    }

    const confirmed = window.confirm(
      `Replace “${row.title}” with ${file.name}? The previous file will remain in audit history.`,
    );
    if (!confirmed) return;

    try {
      setBusy(`REPLACE_${row.id}`);
      const currentVersion = Number(
        row.record?.signedVersion || row.record?.version || 0,
      );
      const nextVersion = currentVersion + 1;
      const uploadedAtIso = new Date().toISOString();
      const fileName = safeFileName(file.name, `${row.code}_replacement.pdf`);
      const storagePath = [
        "nafed_pmc",
        user.uid,
        project.id,
        "document_replacements",
        row.id,
        `v${nextVersion}_${Date.now()}_${fileName}`,
      ].join("/");
      const storageRef = ref(storage, storagePath);
      const sha256 = await sha256File(file);

      await uploadBytes(storageRef, file, {
        contentType: "application/pdf",
        customMetadata: {
          projectId: project.id,
          documentType: row.id,
          uploadedByUid: user.uid,
          uploadedByRole: "PMC",
          version: String(nextVersion),
          sha256: sha256 || "NOT_AVAILABLE",
        },
      });
      const url = await getDownloadURL(storageRef);

      const replacement = {
        type: row.id,
        title: row.title,
        code: row.code,
        url,
        fileName,
        storagePath,
        contentType: "application/pdf",
        size: file.size,
        sha256,
        version: nextVersion,
        signedVersion: nextVersion,
        status: "PMC_REPLACEMENT_ACTIVE",
        source: "PMC_REPLACEMENT",
        uploadedByUid: user.uid,
        uploadedByEmail: user.email || "",
        uploadedByName:
          profile.organizationName ||
          profile.agencyName ||
          profile.name ||
          user.displayName ||
          "PMC Authorised Officer",
        uploadedAtIso,
      };
      const override = {
        action: "REPLACED",
        activeVersion: nextVersion,
        activeUrl: url,
        updatedByUid: user.uid,
        updatedAtIso: uploadedAtIso,
      };

      await updateDoc(
        doc(
          db,
          scope.collectionName || NAFED_COLLECTIONS.godownProjects,
          project.id,
        ),
        {
          [`pmcVerificationDocuments.${row.id}`]: replacement,
          [`pmcDocumentOverrides.${row.id}`]: override,
          pmcDocumentHistory: arrayUnion({
            action: "REPLACED",
            documentType: row.id,
            documentTitle: row.title,
            previousUrl: row.record?.url || "",
            previousVersion: currentVersion,
            newUrl: url,
            newVersion: nextVersion,
            performedByUid: user.uid,
            performedByEmail: user.email || "",
            performedAtIso: uploadedAtIso,
          }),
          documentStage: "PMC_DOCUMENTS_READY_FOR_MOU",
          status: "DOCUMENTS_SUBMITTED_TO_PMC",
          currentStage: "PMC Documents Ready for MOU Confirmation",
          pmcDecision: "PENDING",
          mouStage: "PENDING_DOCUMENT_CONFIRMATION",
          mouStatus: "PENDING_DOCUMENT_CONFIRMATION",
          updatedAt: serverTimestamp(),
        },
      );

      patchLocalProject(project.id, {
        pmcVerificationDocuments: {
          ...(project.pmcVerificationDocuments || {}),
          [row.id]: replacement,
        },
        pmcDocumentOverrides: {
          ...(project.pmcDocumentOverrides || {}),
          [row.id]: override,
        },
        documentStage: "PMC_DOCUMENTS_READY_FOR_MOU",
        status: "DOCUMENTS_SUBMITTED_TO_PMC",
        currentStage: "PMC Documents Ready for MOU Confirmation",
        pmcDecision: "PENDING",
        mouStage: "PENDING_DOCUMENT_CONFIRMATION",
        mouStatus: "PENDING_DOCUMENT_CONFIRMATION",
      });
      toast.success(`${row.title} replaced successfully.`);
    } catch (error) {
      console.error(error);
      toast.error(permissionMessage(error, "Document replacement failed."));
    } finally {
      setBusy("");
    }
  };

  const removeDocument = async (row) => {
    const user = auth.currentUser;
    if (!project || !user || !row.record?.url) return;
    if (mouLocked) return toast.error("MOU signing has started; documents are locked.");
    const confirmed = window.confirm(
      `Remove “${row.title}” from the active document set? The previous file will remain in audit history and can be replaced later.`,
    );
    if (!confirmed) return;

    try {
      setBusy(`REMOVE_${row.id}`);
      const removedAtIso = new Date().toISOString();
      const override = {
        action: "REMOVED",
        previousUrl: row.record.url,
        previousStoragePath: row.record.storagePath || "",
        previousVersion: Number(
          row.record.signedVersion || row.record.version || 1,
        ),
        updatedByUid: user.uid,
        updatedByEmail: user.email || "",
        updatedAtIso: removedAtIso,
      };

      await updateDoc(
        doc(
          db,
          scope.collectionName || NAFED_COLLECTIONS.godownProjects,
          project.id,
        ),
        {
          [`pmcDocumentOverrides.${row.id}`]: override,
          pmcDocumentHistory: arrayUnion({
            action: "REMOVED_FROM_ACTIVE_SET",
            documentType: row.id,
            documentTitle: row.title,
            previousUrl: row.record.url,
            previousStoragePath: row.record.storagePath || "",
            previousVersion: override.previousVersion,
            performedByUid: user.uid,
            performedByEmail: user.email || "",
            performedAtIso: removedAtIso,
          }),
          documentStage: "PMC_DOCUMENTS_INCOMPLETE",
          status: "DOCUMENTS_SUBMITTED_TO_PMC",
          currentStage: "PMC Document Replacement Required",
          pmcDecision: "PENDING",
          mouStage: "PENDING_DOCUMENT_CONFIRMATION",
          mouStatus: "PENDING_DOCUMENT_CONFIRMATION",
          updatedAt: serverTimestamp(),
        },
      );

      patchLocalProject(project.id, {
        pmcDocumentOverrides: {
          ...(project.pmcDocumentOverrides || {}),
          [row.id]: override,
        },
        documentStage: "PMC_DOCUMENTS_INCOMPLETE",
        status: "DOCUMENTS_SUBMITTED_TO_PMC",
        currentStage: "PMC Document Replacement Required",
        pmcDecision: "PENDING",
        mouStage: "PENDING_DOCUMENT_CONFIRMATION",
        mouStatus: "PENDING_DOCUMENT_CONFIRMATION",
      });
      toast.success("Document removed from the active set. Upload its replacement.");
    } catch (error) {
      console.error(error);
      toast.error(permissionMessage(error, "Document could not be removed."));
    } finally {
      setBusy("");
    }
  };

  const continueToMou = async () => {
    const user = auth.currentUser;
    if (!project || !user) return toast.error("Project or PMC session is unavailable.");
    if (mouLocked) {
      onOpenMou?.();
      return;
    }
    if (mandatoryMissing.length) {
      return toast.error(
        `Upload the missing document first: ${mandatoryMissing[0].title}`,
      );
    }

    const confirmed = window.confirm(
      "Confirm that all mandatory PACS documents are complete and continue to the official PMC–PACS MOU?",
    );
    if (!confirmed) return;

    try {
      setBusy("MOU");
      // Force a fresh Firebase token before calling the trusted backend.
      await user.getIdToken(true);

      const approveDocumentsAndOpenMou = httpsCallable(
        functions,
        "approvePmcDocumentsAndOpenMou",
      );
      const response = await approveDocumentsAndOpenMou({
        projectId: project.id,
        applicationId: getApplicationId(project),
      });
      const result = response?.data || {};
      if (result.ok !== true) {
        throw new Error(result.message || "PMC document approval failed.");
      }

      const changes = result.changes || {
        documentStage: "PMC_DOCUMENTS_APPROVED",
        status: "PMC_DOCUMENTS_APPROVED",
        currentStage: "MOU Pending PMC Signature",
        pmcDecision: "APPROVED",
        documentQueryStatus: "NO_OPEN_QUERY",
        pmcDocumentApproval: result.approval || {},
        mouStage: "PENDING_PMC_SIGNATURE",
        mouStatus: "PENDING_PMC_SIGNATURE",
        completedStep: 3,
        progress: Math.max(Number(project.progress || 0), 35),
        pmcDocumentsApprovedAtIso: result.approvedAtIso || "",
      };
      patchLocalProject(project.id, changes);

      toast.success("Documents confirmed. Opening the MOU desk...");
      onOpenMou?.();
    } catch (error) {
      console.error(error);
      toast.error(permissionMessage(error, "MOU stage could not be opened."));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <style>{STYLE}</style>
      <main className="simple-doc-desk">
        <header className="doc-hero">
          <div>
            <small>PMC DOCUMENT DESK</small>
            <h1>PACS Documents → Direct MOU</h1>
            <p>
              View every PACS PDF, download, archive, replace or download the
              complete set as one ZIP. Confirm the set to open MOU execution.
            </p>
          </div>
          <button
            type="button"
            className="refresh-button"
            onClick={() => loadProjects(true)}
            disabled={loading || Boolean(busy)}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </header>

        <section className="doc-summary">
          <article><span>Assigned PACS Projects</span><b>{projects.length}</b></article>
          <article><span>Available Documents</span><b>{availableCount}</b></article>
          <article className={mandatoryMissing.length ? "warning" : "ready"}>
            <span>Mandatory Missing</span><b>{mandatoryMissing.length}</b>
          </article>
        </section>

        {loading ? (
          <section className="empty-panel">Loading assigned PACS documents...</section>
        ) : !projects.length ? (
          <section className="empty-panel">
            No allocated NAFED PACS project is available for this PMC.
          </section>
        ) : (
          <>
            <section className="project-bar">
              <label>
                <span>Select PACS Project</span>
                <select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
                  {projects.map((item) => (
                    <option key={item.id} value={item.id}>
                      {getPacsName(item)} — {getApplicationId(item)}
                    </option>
                  ))}
                </select>
              </label>
              {project && (
                <div className="project-facts">
                  <div><span>PACS</span><b>{getPacsName(project)}</b></div>
                  <div><span>Application</span><b>{getApplicationId(project)}</b></div>
                  <div><span>District / State</span><b>{project.district || "-"} / {project.state || "-"}</b></div>
                  <div><span>Stage</span><b>{project.currentStage || project.documentStage || "Document Review"}</b></div>
                </div>
              )}
            </section>

            <section className="documents-card">
              <div className="documents-head">
                <div>
                  <small>ALL PACS FILES</small>
                  <h2>Document Register</h2>
                  <p>Removal is audit-safe: the old file remains in history and is not silently destroyed.</p>
                </div>
                <button
                  type="button"
                  className="zip-button"
                  onClick={downloadAllAsZip}
                  disabled={!availableCount || Boolean(busy)}
                >
                  {busy === "ZIP" ? "Preparing ZIP..." : `Download All (${availableCount}) ZIP`}
                </button>
              </div>

              <div className="document-table-wrap">
                <table className="document-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Document</th>
                      <th>Active File</th>
                      <th>Version / Date</th>
                      <th>SHA-256</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const operationBusy = busy.endsWith(`_${row.id}`);
                      return (
                        <tr key={row.id} className={row.record?.url ? "" : "missing-row"}>
                          <td><span className="row-number">{index + 1}</span></td>
                          <td>
                            <b>{row.title}</b>
                            <small>{row.code} · {row.optional ? "Additional" : "Mandatory"}</small>
                          </td>
                          <td>
                            {row.record?.url ? (
                              <>
                                <span className="file-ok">Available</span>
                                <small>{row.record.sourceLabel}</small>
                                <small title={row.record.fileName || ""}>{row.record.fileName || "PDF document"}</small>
                              </>
                            ) : (
                              <span className="file-missing">Missing / Removed</span>
                            )}
                          </td>
                          <td>
                            <b>Version {row.record?.signedVersion || row.record?.version || "-"}</b>
                            <small>{formatDate(row.record?.uploadedAtIso || row.record?.submittedAtIso || row.record?.createdAt)}</small>
                          </td>
                          <td>
                            <code title={row.record?.sha256 || "Not available"}>
                              {row.record?.sha256 ? `${row.record.sha256.slice(0, 12)}…` : "Not available"}
                            </code>
                          </td>
                          <td>
                            <div className="row-actions">
                              <button
                                type="button"
                                className="view"
                                disabled={!row.record?.url}
                                onClick={() => window.open(row.record.url, "_blank", "noopener,noreferrer")}
                              >View</button>
                              <button
                                type="button"
                                className="download"
                                disabled={!row.record?.url || Boolean(busy)}
                                onClick={() => downloadDocument(row)}
                              >Download</button>
                              <button
                                type="button"
                                className="remove"
                                disabled={!row.record?.url || mouLocked || Boolean(busy)}
                                onClick={() => removeDocument(row)}
                              >Remove</button>
                              <label className={`replace ${mouLocked || Boolean(busy) ? "disabled" : ""}`}>
                                <input
                                  type="file"
                                  accept="application/pdf,.pdf"
                                  disabled={mouLocked || Boolean(busy)}
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = "";
                                    replaceDocument(row, file);
                                  }}
                                />
                                {operationBusy ? "Working..." : row.record?.url ? "Replace" : "Upload"}
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={`mou-bar ${mandatoryMissing.length ? "blocked" : "ready"}`}>
              <div>
                <small>{mandatoryMissing.length ? "ACTION REQUIRED" : "DOCUMENT SET COMPLETE"}</small>
                <h2>
                  {mandatoryMissing.length
                    ? `${mandatoryMissing.length} mandatory document(s) missing`
                    : "Continue to Official PMC–PACS MOU"}
                </h2>
                <p>
                  {mandatoryMissing.length
                    ? `Upload ${mandatoryMissing[0].title} to enable MOU.`
                    : "No separate remarks, GPS or individual approval steps are required in this simplified desk."}
                </p>
              </div>
              <button
                type="button"
                onClick={continueToMou}
                disabled={Boolean(busy) || Boolean(mandatoryMissing.length)}
              >
                {busy === "MOU" ? "Opening MOU..." : mouLocked ? "Open MOU Desk" : "Confirm Documents & Open MOU"}
              </button>
            </section>
          </>
        )}
      </main>
    </>
  );
}

const STYLE = `
.simple-doc-desk{display:grid;gap:16px;color:#14263f;font-family:Inter,Arial,sans-serif}.doc-hero{display:flex;justify-content:space-between;gap:20px;align-items:center;padding:24px;border:1px solid #cddbeb;border-radius:18px;background:linear-gradient(125deg,#062f65,#087047);color:#fff;box-shadow:0 14px 35px rgba(20,38,63,.12)}.doc-hero small,.documents-head small,.mou-bar small{font-size:10px;font-weight:900;letter-spacing:.1em}.doc-hero small{color:#9ef0bd}.doc-hero h1{margin:5px 0 7px;font-size:27px}.doc-hero p{max-width:760px;margin:0;color:#e4f5ed;font-size:12px;line-height:1.6}.refresh-button,.zip-button,.mou-bar button{border:0;border-radius:10px;padding:11px 15px;font-size:11px;font-weight:900;cursor:pointer}.refresh-button{background:#fff;color:#07533c}.refresh-button:disabled,.zip-button:disabled,.mou-bar button:disabled{cursor:not-allowed;opacity:.55}.doc-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.doc-summary article{padding:17px;border:1px solid #d4e0ed;border-radius:14px;background:#fff;box-shadow:0 8px 25px rgba(20,38,63,.06)}.doc-summary span,.doc-summary b{display:block}.doc-summary span{font-size:10px;font-weight:800;color:#65788f;text-transform:uppercase}.doc-summary b{margin-top:5px;font-size:27px;color:#143766}.doc-summary .warning{border-color:#f2c77c;background:#fff9ed}.doc-summary .warning b{color:#a96500}.doc-summary .ready{border-color:#9bd4aa;background:#f3fbf5}.doc-summary .ready b{color:#14743e}.empty-panel{padding:55px 20px;text-align:center;border:1px dashed #aebfd2;border-radius:16px;background:#fff;color:#6a7c91;font-size:13px;font-weight:700}.project-bar{display:grid;grid-template-columns:minmax(300px,420px) 1fr;gap:14px;padding:16px;border:1px solid #d4e0ed;border-radius:14px;background:#fff}.project-bar label span{display:block;margin-bottom:6px;font-size:9px;font-weight:900;color:#61758b;text-transform:uppercase}.project-bar select{width:100%;min-height:43px;border:1px solid #b8c9db;border-radius:9px;background:#fff;padding:0 11px;color:#17324d;font-weight:800}.project-facts{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}.project-facts div{padding:9px 10px;border-left:3px solid #3d83c6;background:#f4f8fc}.project-facts span,.project-facts b{display:block}.project-facts span{font-size:8px;color:#71849a;text-transform:uppercase}.project-facts b{margin-top:4px;font-size:10px;overflow-wrap:anywhere}.documents-card{border:1px solid #cddbeb;border-radius:17px;background:#fff;overflow:hidden;box-shadow:0 10px 30px rgba(20,38,63,.07)}.documents-head{display:flex;justify-content:space-between;gap:15px;align-items:center;padding:18px 20px;border-bottom:1px solid #dbe5ef}.documents-head small{color:#287347}.documents-head h2{margin:4px 0;font-size:20px}.documents-head p{margin:0;color:#6b7d91;font-size:10px}.zip-button{background:#173f74;color:#fff;white-space:nowrap}.document-table-wrap{overflow:auto}.document-table{width:100%;min-width:1120px;border-collapse:collapse}.document-table th{padding:11px 12px;background:#eef4f9;color:#536b83;font-size:9px;text-align:left;text-transform:uppercase;letter-spacing:.06em}.document-table td{padding:13px 12px;border-top:1px solid #e1e8f0;vertical-align:middle;font-size:10px}.document-table td>b,.document-table td>small{display:block}.document-table td>small{margin-top:4px;color:#708197}.missing-row{background:#fffaf1}.row-number{display:grid;width:31px;height:31px;place-items:center;border-radius:50%;background:#e7f1fd;color:#1a5fa7;font-weight:950}.file-ok,.file-missing{display:inline-flex;border-radius:999px;padding:5px 8px;font-size:8px;font-weight:950}.file-ok{background:#e5f6e9;color:#16713d}.file-missing{background:#ffefde;color:#a75f00}.document-table code{font-size:9px;color:#4f6378}.row-actions{display:flex;flex-wrap:wrap;gap:5px}.row-actions button,.row-actions .replace{border:1px solid #b7c8da;border-radius:7px;padding:7px 8px;background:#fff;color:#173a64;font-size:9px;font-weight:900;cursor:pointer}.row-actions .download{background:#eaf3ff}.row-actions .remove{border-color:#e8a7a1;color:#a8291f;background:#fff4f2}.row-actions .replace{border-color:#91c49e;background:#eff9f1;color:#14703b}.row-actions .replace input{display:none}.row-actions button:disabled,.row-actions .replace.disabled{cursor:not-allowed;opacity:.42}.mou-bar{display:flex;justify-content:space-between;gap:20px;align-items:center;padding:20px 22px;border-radius:16px}.mou-bar.ready{border:1px solid #8ec9a0;background:linear-gradient(110deg,#eefaf1,#e8f4ff)}.mou-bar.blocked{border:1px solid #efc878;background:#fff9eb}.mou-bar small{color:#247244}.mou-bar.blocked small{color:#a66500}.mou-bar h2{margin:4px 0;font-size:19px}.mou-bar p{margin:0;color:#63778c;font-size:11px}.mou-bar button{background:linear-gradient(135deg,#087344,#184e85);color:#fff;white-space:nowrap}
@media(max-width:900px){.project-bar{grid-template-columns:1fr}.project-facts{grid-template-columns:repeat(2,1fr)}.doc-hero,.documents-head,.mou-bar{align-items:flex-start;flex-direction:column}.doc-summary{grid-template-columns:1fr}.zip-button,.mou-bar button{width:100%}}
`;
